<main class="guided-page">
  <header class="guided-header">
    <button mat-button type="button" (click)="backToWorkflows()"><mat-icon>arrow_back</mat-icon> All workflows</button>
    <span class="product-label"><mat-icon>account_tree</mat-icon> Shine Connect</span>
    <h1>{{saved ? 'Edit your workflow' : 'Create a workflow'}}</h1>
    <p>Build the real workflow as an ordered list. Add steps, change their order and configure nested paths without drawing a technical diagram.</p>
  </header>

  @if (loading) {
    <section class="state"><mat-spinner diameter="34"></mat-spinner><span>Opening your workflow…</span></section>
  } @else if (loadError) {
    <section class="state error" role="alert">
      <mat-icon>error_outline</mat-icon>
      <strong>This workflow is not supported by the new editor yet.</strong>
      <span>Nothing was changed. You can still view it in the temporary legacy inventory.</span>
      <button mat-stroked-button type="button" (click)="backToAdvancedWorkflows()">View legacy workflows</button>
    </section>
  } @else {
    <section class="builder-layout">
      <aside class="step-library" aria-labelledby="library-title">
        <span class="eyebrow">Workflow details</span>
        <form [formGroup]="workflowForm" (ngSubmit)="save()">
          <mat-form-field appearance="outline">
            <mat-label>Workflow name</mat-label>
            <input matInput formControlName="name" />
            @if (workflowForm.controls.name.touched && workflowForm.controls.name.invalid) {
              <mat-error>Add a short, familiar name.</mat-error>
            }
          </mat-form-field>

          <h2 id="library-title">Add a step</h2>
          <p>Choose what should happen next. You can move it afterwards.</p>
          <div class="step-buttons">
            <button mat-stroked-button type="button"
                    [disabled]="!canAddStep(topLevelSteps, 'prepare-information')"
                    (click)="addTopLevelStep('prepare-information')">
              <mat-icon>tune</mat-icon><span><strong>Prepare information</strong><small>Choose and shape request details</small></span>
            </button>
            <button mat-stroked-button type="button"
                    [disabled]="!localPreviewAvailable || !canAddStep(topLevelSteps, 'verify-local-account')"
                    (click)="addTopLevelStep('verify-local-account')">
              <mat-icon>verified_user</mat-icon><span><strong>Check an account</strong><small>Use an approved connected account</small></span>
            </button>
            <button mat-stroked-button type="button"
                    [disabled]="!canAddStep(topLevelSteps, 'for-each-item')"
                    (click)="addTopLevelStep('for-each-item')">
              <mat-icon>repeat</mat-icon><span><strong>For every item</strong><small>Repeat a nested step safely</small></span>
            </button>
            <button mat-stroked-button type="button"
                    [disabled]="moduleCatalogState !== 'ready' || !canAddStep(topLevelSteps, 'prepare-information')"
                    (click)="openModulePicker(moduleLocation('top'))">
              <mat-icon>extension</mat-icon><span><strong>Use a connected app</strong><small>Choose from the capabilities Connect supports</small></span>
            </button>
            @if (!decision) {
              <button mat-stroked-button type="button" (click)="addDecision()">
                <mat-icon>alt_route</mat-icon><span><strong>Add a decision</strong><small>Only when the workflow needs two possible paths</small></span>
              </button>
            }
          </div>

          @if (moduleCatalogState === 'loading') {
            <div class="catalog-state"><mat-spinner diameter="18"></mat-spinner><span>Loading available app modules…</span></div>
          } @else if (moduleCatalogState === 'empty') {
            <div class="catalog-state"><mat-icon>inventory_2</mat-icon><span>No app modules are available for this workspace yet.</span></div>
          } @else if (moduleCatalogState === 'error') {
            <div class="catalog-state error"><mat-icon>warning_amber</mat-icon><span>App modules could not be loaded. Built-in steps still work.</span></div>
          }

          @if (moduleEditor; as editor) {
            <section class="module-picker" aria-labelledby="module-picker-title">
              <div class="module-picker-heading">
                <div><span class="eyebrow">Connected app</span><h3 id="module-picker-title">Configure the step</h3></div>
                <button mat-icon-button type="button" (click)="closeModulePicker()" aria-label="Close connected app editor"><mat-icon>close</mat-icon></button>
              </div>
              <mat-form-field appearance="outline">
                <mat-label>Module</mat-label>
                <mat-select [value]="editor.node.paletteId" (selectionChange)="selectModuleNode($event.value)">
                  @for (module of moduleCatalog; track module.paletteId) {
                    <mat-option [value]="module.paletteId">{{module.label}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <app-connect-guided-module-editor
                [node]="editor.node"
                [initialDraft]="editor.draft"
                [allowCurrentItemBindings]="editor.location.loopIndex !== undefined"
                [priorStepBindings]="priorStepBindingsFor(editor.location)"
                (draftReady)="setModuleDraft($event)" />
              <mat-form-field appearance="outline">
                <mat-label>Connected account</mat-label>
                <mat-select [value]="editor.connectionId" (selectionChange)="selectModuleConnection($event.value)">
                  @for (account of moduleAccounts; track account.id) {
                    <mat-option [value]="account.id">{{account.name}}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (moduleAccountState === 'loading') {
                <div class="account-state"><mat-spinner diameter="18"></mat-spinner><span>Checking available accounts…</span></div>
              } @else if (moduleAccountState === 'empty') {
                <div class="account-state error" role="status">
                  <mat-icon>link_off</mat-icon>
                  <span><strong>No ready account is available for this app.</strong><small>Connect or repair the account, then return and choose this step again.</small></span>
                  <a mat-stroked-button href="/connections" target="_blank" rel="noopener">Open Connections</a>
                </div>
              } @else if (moduleAccountState === 'error') {
                <div class="account-state error" role="alert">
                  <mat-icon>sync_problem</mat-icon>
                  <span><strong>Connected accounts could not be loaded.</strong><small>Try again. Your saved workflow and accounts have not been changed.</small></span>
                  <button mat-stroked-button type="button" (click)="retryModuleAccountInventory()">Try again</button>
                </div>
              }
              <button mat-flat-button color="primary" type="button"
                      [disabled]="moduleAccountState !== 'ready' || !editor.draft || !editor.connectionId"
                      (click)="applyModule()">
                <mat-icon>check</mat-icon> Use this step
              </button>
            </section>
          }

          <div class="library-note">
            <mat-icon>info</mat-icon>
            <span>Decisions and loops are part of the saved workflow—not presentation-only notes.</span>
          </div>

          @if (saveError) {
            <div class="callout error" role="alert">Connect could not save this workflow. Review the steps and try again.</div>
          }
          <button class="save-button" mat-flat-button color="primary" type="submit" [disabled]="saving || moduleEditor !== null">
            <mat-spinner [class.hidden]="!saving" diameter="20"></mat-spinner>
            <mat-icon [class.hidden]="saving">save</mat-icon>
            {{saving ? 'Saving…' : (saved ? 'Save changes' : 'Create workflow')}}
          </button>
        </form>
      </aside>

      <section class="workflow-outline" aria-labelledby="outline-title">
        <div class="outline-heading">
          <div><span class="eyebrow">Workflow</span><h2 id="outline-title">{{draft.name || 'Untitled workflow'}}</h2></div>
          <span class="outline-status"><mat-icon>format_list_numbered</mat-icon> Ordered steps</span>
        </div>

        <article class="step-card trigger-card">
          <span class="step-number">Start</span>
          <span class="step-icon"><mat-icon>play_circle</mat-icon></span>
          <div class="step-copy"><strong>When a request is started</strong><span>Receive a subject, details, priority and optional item list.</span></div>
        </article>

        @for (step of topLevelSteps; track $index; let index = $index) {
          <div class="connector" aria-hidden="true"></div>
          <article class="step-card" [class.loop-card]="step.kind === 'for-each-item'">
            <span class="step-number">{{index + 1}}</span>
            <span class="step-icon">
              <mat-icon>{{step.kind === 'prepare-information' ? 'tune' : step.kind === 'verify-local-account' ? 'verified_user' : step.kind === 'connector-operation' ? 'extension' : 'repeat'}}</mat-icon>
            </span>
            <div class="step-copy">
              <strong>{{stepTitle(step)}}</strong>
              <span>{{stepDescription(step)}}</span>
              @if (step.kind === 'verify-local-account') {
                <div class="plain-config"><span>Connected account</span><strong>Intuvio local preview</strong></div>
                @if (!localPreviewAvailable) {
                  <small class="inline-error">This account is unavailable. The workflow can be edited but cannot be tested.</small>
                }
              }
              @if (step.kind === 'prepare-information') {
                <div class="field-chips" aria-label="Information kept by this step"><span>Subject</span><span>Details</span><span>Priority</span></div>
              }
              @if (step.kind === 'connector-operation') {
                <div class="module-summary">
                  <span class="effect-badge" [class.write]="moduleEffect(step) === 'write'" [class.delete]="moduleEffect(step) === 'delete'">{{moduleEffect(step) ? effectLabel(moduleEffect(step)!) : 'Unavailable'}}</span>
                  <span><small>Connected account</small><strong>{{moduleAccountName(step)}}</strong></span>
                  <button mat-button type="button" (click)="openModulePicker(moduleLocation('top', index), moduleNode(step) ?? undefined)"><mat-icon>edit</mat-icon> Change</button>
                </div>
              }
              @if (step.kind === 'for-each-item') {
                <ng-container *ngTemplateOutlet="loopConfiguration; context: {$implicit: step, blocks: topLevelSteps, index: index, container: 'top', expanded: true}"></ng-container>
              }
            </div>
            <div class="step-controls" aria-label="Step controls">
              <button mat-icon-button type="button" [disabled]="index === 0" (click)="moveTopLevelStep(index, -1)" [attr.aria-label]="'Move ' + stepTitle(step) + ' up'"><mat-icon>arrow_upward</mat-icon></button>
              <button mat-icon-button type="button" [disabled]="index === topLevelSteps.length - 1" (click)="moveTopLevelStep(index, 1)" [attr.aria-label]="'Move ' + stepTitle(step) + ' down'"><mat-icon>arrow_downward</mat-icon></button>
              <button mat-icon-button type="button" (click)="removeTopLevelStep(index)" [attr.aria-label]="'Remove ' + stepTitle(step)"><mat-icon>delete_outline</mat-icon></button>
            </div>
          </article>
        }

        @if (decision; as decision) {
        <div class="connector" aria-hidden="true"></div>
        <article class="decision-card">
          <div class="decision-heading">
            <span class="step-number">{{topLevelSteps.length + 1}}</span>
            <span class="step-icon"><mat-icon>alt_route</mat-icon></span>
            <div class="step-copy"><strong>Choose a path</strong><span>If {{decisionSummary}}, continue down the matching path.</span></div>
            <button mat-stroked-button type="button"
                    [disabled]="decision.urgent.blocks.length > 0 || decision.standard.blocks.length > 0"
                    (click)="removeDecision()">
              <mat-icon>delete_outline</mat-icon> Remove decision
            </button>
          </div>
          <div class="decision-rule" aria-label="Decision rule">
            <mat-form-field appearance="outline">
              <mat-label>Information to check</mat-label>
              <mat-select [ngModel]="decisionCondition?.field"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="setDecisionField($event)">
                <mat-option value="priority">Priority</mat-option>
                <mat-option value="subject">Subject</mat-option>
                <mat-option value="details">Details</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>How to compare</mat-label>
              <mat-select [ngModel]="decisionCondition?.operator"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="setDecisionOperator($event)">
                <mat-option value="equals">Is</mat-option>
                <mat-option value="not-equals">Is not</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Value</mat-label>
              <input matInput [ngModel]="decisionCondition?.value"
                     [ngModelOptions]="{standalone: true}"
                     (ngModelChange)="setDecisionValue($event)"
                     (paste)="onDecisionValuePaste($event)"
                     (beforeinput)="onDecisionValueBeforeInput($event)"
                     [attr.aria-invalid]="decisionValueInputError"
                     aria-describedby="decision-value-guidance"
                     aria-label="Decision value"
                     maxlength="120" />
            </mat-form-field>
            @if (decisionValueInputError) {
              <span class="inline-error" role="alert">Use plain text without quotation marks, backslashes or line breaks.</span>
            }
            <span id="decision-value-guidance" class="decision-value-guidance">Plain text only—no quotation marks, backslashes or line breaks.</span>
            <span class="decision-preview"><mat-icon>call_split</mat-icon> If {{decisionSummary}}</span>
          </div>
          <div class="decision-branches">
            <section class="branch" aria-labelledby="urgent-branch-title">
              <span class="branch-label">Matches</span>
              <h3 id="urgent-branch-title">Matching path</h3>
              @for (step of decision.urgent.blocks; track $index; let index = $index) {
                <article class="nested-action" [class.nested-loop]="step.kind === 'for-each-item'">
                  <mat-icon>{{step.kind === 'prepare-information' ? 'tune' : step.kind === 'verify-local-account' ? 'verified_user' : step.kind === 'connector-operation' ? 'extension' : 'repeat'}}</mat-icon>
                  <div><strong>{{stepTitle(step)}}</strong><span>{{stepDescription(step)}}</span></div>
                  <span class="nested-controls">
                    <button mat-icon-button type="button" [disabled]="index === 0" (click)="moveBranchStep('urgent', index, -1)" [attr.aria-label]="'Move urgent-path ' + stepTitle(step) + ' up'"><mat-icon>arrow_upward</mat-icon></button>
                    <button mat-icon-button type="button" [disabled]="index === decision.urgent.blocks.length - 1" (click)="moveBranchStep('urgent', index, 1)" [attr.aria-label]="'Move urgent-path ' + stepTitle(step) + ' down'"><mat-icon>arrow_downward</mat-icon></button>
                    <button mat-icon-button type="button" (click)="removeBranchStep('urgent', index)" [attr.aria-label]="'Remove urgent-path ' + stepTitle(step)"><mat-icon>delete_outline</mat-icon></button>
                  </span>
                </article>
                @if (step.kind === 'for-each-item') {
                  <ng-container *ngTemplateOutlet="loopConfiguration; context: {$implicit: step, blocks: decision.urgent.blocks, index: index, container: 'urgent'}"></ng-container>
                } @else if (step.kind === 'connector-operation') {
                  <div class="nested-module-meta"><span>{{moduleAccountName(step)}}</span><span>{{moduleEffect(step) ? effectLabel(moduleEffect(step)!) : 'Unavailable'}}</span><button mat-button type="button" (click)="openModulePicker(moduleLocation('urgent', index), moduleNode(step) ?? undefined)">Change</button></div>
                }
              }
              <div class="branch-add">
                <button mat-button type="button" [disabled]="!canAddStep(decision.urgent.blocks, 'prepare-information')" (click)="addBranchStep('urgent', 'prepare-information')"><mat-icon>add</mat-icon> Prepare information</button>
                <button mat-button type="button" [disabled]="!localPreviewAvailable || !canAddStep(decision.urgent.blocks, 'verify-local-account')" (click)="addBranchStep('urgent', 'verify-local-account')"><mat-icon>add</mat-icon> Check account</button>
                <button mat-button type="button" [disabled]="!canAddStep(decision.urgent.blocks, 'for-each-item')" (click)="addBranchStep('urgent', 'for-each-item')"><mat-icon>add</mat-icon> For every item</button>
                <button mat-button type="button" [disabled]="moduleCatalogState !== 'ready' || !canAddStep(decision.urgent.blocks, 'prepare-information')" (click)="openModulePicker(moduleLocation('urgent'))"><mat-icon>add</mat-icon> Connected app</button>
              </div>
              <mat-form-field appearance="outline">
                <mat-label>Final outcome</mat-label>
                <input matInput [(ngModel)]="decision.urgent.outcome" [ngModelOptions]="{standalone: true}" maxlength="120" />
              </mat-form-field>
            </section>

            <section class="branch" aria-labelledby="standard-branch-title">
              <span class="branch-label">Otherwise</span>
              <h3 id="standard-branch-title">Otherwise path</h3>
              @for (step of decision.standard.blocks; track $index; let index = $index) {
                <article class="nested-action" [class.nested-loop]="step.kind === 'for-each-item'">
                  <mat-icon>{{step.kind === 'prepare-information' ? 'tune' : step.kind === 'verify-local-account' ? 'verified_user' : step.kind === 'connector-operation' ? 'extension' : 'repeat'}}</mat-icon>
                  <div><strong>{{stepTitle(step)}}</strong><span>{{stepDescription(step)}}</span></div>
                  <span class="nested-controls">
                    <button mat-icon-button type="button" [disabled]="index === 0" (click)="moveBranchStep('standard', index, -1)" [attr.aria-label]="'Move normal-path ' + stepTitle(step) + ' up'"><mat-icon>arrow_upward</mat-icon></button>
                    <button mat-icon-button type="button" [disabled]="index === decision.standard.blocks.length - 1" (click)="moveBranchStep('standard', index, 1)" [attr.aria-label]="'Move normal-path ' + stepTitle(step) + ' down'"><mat-icon>arrow_downward</mat-icon></button>
                    <button mat-icon-button type="button" (click)="removeBranchStep('standard', index)" [attr.aria-label]="'Remove normal-path ' + stepTitle(step)"><mat-icon>delete_outline</mat-icon></button>
                  </span>
                </article>
                @if (step.kind === 'for-each-item') {
                  <ng-container *ngTemplateOutlet="loopConfiguration; context: {$implicit: step, blocks: decision.standard.blocks, index: index, container: 'standard'}"></ng-container>
                } @else if (step.kind === 'connector-operation') {
                  <div class="nested-module-meta"><span>{{moduleAccountName(step)}}</span><span>{{moduleEffect(step) ? effectLabel(moduleEffect(step)!) : 'Unavailable'}}</span><button mat-button type="button" (click)="openModulePicker(moduleLocation('standard', index), moduleNode(step) ?? undefined)">Change</button></div>
                }
              }
              <div class="branch-add">
                <button mat-button type="button" [disabled]="!canAddStep(decision.standard.blocks, 'prepare-information')" (click)="addBranchStep('standard', 'prepare-information')"><mat-icon>add</mat-icon> Prepare information</button>
                <button mat-button type="button" [disabled]="!localPreviewAvailable || !canAddStep(decision.standard.blocks, 'verify-local-account')" (click)="addBranchStep('standard', 'verify-local-account')"><mat-icon>add</mat-icon> Check account</button>
                <button mat-button type="button" [disabled]="!canAddStep(decision.standard.blocks, 'for-each-item')" (click)="addBranchStep('standard', 'for-each-item')"><mat-icon>add</mat-icon> For every item</button>
                <button mat-button type="button" [disabled]="moduleCatalogState !== 'ready' || !canAddStep(decision.standard.blocks, 'prepare-information')" (click)="openModulePicker(moduleLocation('standard'))"><mat-icon>add</mat-icon> Connected app</button>
              </div>
              <mat-form-field appearance="outline">
                <mat-label>Final outcome</mat-label>
                <input matInput [(ngModel)]="decision.standard.outcome" [ngModelOptions]="{standalone: true}" maxlength="120" />
              </mat-form-field>
            </section>
          </div>
        </article>
        }
      </section>
    </section>

    @if (saved) {
      <section class="test-panel" aria-labelledby="test-title">
        <div class="test-heading"><div><span class="eyebrow">Safe local test</span><h2 id="test-title">Run the workflow you can see above</h2><p>Example information only. {{usesLocalPreview || hasModules ? 'Only the accounts named in the workflow are used.' : 'No connected account is used.'}}</p></div><span class="saved-badge"><mat-icon>cloud_done</mat-icon> Saved locally</span></div>
        <form [formGroup]="testForm" (ngSubmit)="run()">
          <div class="test-grid">
            <mat-form-field appearance="outline"><mat-label>Subject</mat-label><input matInput formControlName="subject" /></mat-form-field>
            <mat-form-field appearance="outline"><mat-label>Details</mat-label><input matInput formControlName="details" /></mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Priority</mat-label>
              <mat-select formControlName="priority"><mat-option value="Urgent">Urgent</mat-option><mat-option value="Normal">Normal</mat-option></mat-select>
            </mat-form-field>
          </div>
          @if (hasLoop) {
            <mat-form-field class="items-field" appearance="outline">
              <mat-label>Items to process — one item per row</mat-label>
              <textarea matInput formControlName="items" rows="3"></textarea>
              <mat-hint>Connect will process the first 10 non-empty rows.</mat-hint>
            </mat-form-field>
            @if (loopInputItems.length) {
              <ol class="item-preview" aria-label="Items that will be processed">
                @for (item of loopInputItems; track $index) {<li><span>Item {{$index + 1}}</span></li>}
              </ol>
            }
          }
          @if (hasMutatingModules) {
            <mat-checkbox [(ngModel)]="confirmProviderChanges" [ngModelOptions]="{standalone: true}">
              I understand this workflow changes or removes information in a connected app.
            </mat-checkbox>
          }
          <button class="run-button" mat-flat-button color="primary" type="submit"
                  [disabled]="running || moduleEditor !== null || hasUnsavedChanges || hasMutatingModules && !confirmProviderChanges">
            <mat-spinner [class.hidden]="!running" diameter="20"></mat-spinner>
            <mat-icon [class.hidden]="running">play_arrow</mat-icon>
            {{running ? 'Running workflow…' : 'Run safe test'}}
          </button>
          @if (hasUnsavedChanges) {
            <span class="technical-note"><mat-icon>save</mat-icon> Save your changes before running this test.</span>
          }
        </form>
        @if (runSucceeded) {
          <div class="run-result" role="status"><mat-icon>check_circle</mat-icon><div><strong>Workflow finished successfully.</strong>
            <span>{{completedStepCount}} {{completedStepCount === 1 ? 'step' : 'steps'}} completed.</span>
            @if (runPath) {
              <span>{{runPath === 'urgent' ? 'Matching' : 'Otherwise'}} path completed.</span>
              @if (runOutcome) {<span>{{runOutcome}}</span>}
            }
            @for (loop of loopRunSummaries; track loop.nodeId) {
              <span class="loop-result">Repeated step: {{loop.processed}} items processed · {{loop.succeeded}} completed{{loop.failed ? ' · ' + loop.failed + ' stopped' : ''}}.</span>
            }
          </div></div>
        } @else if (runError) {
          <div class="run-result error" role="alert"><mat-icon>error</mat-icon><div><strong>The safe test stopped.</strong><span>Check the local environment and try again.</span></div></div>
        }
      </section>
    }
  }
</main>

<ng-template #loopConfiguration let-step let-blocks="blocks" let-index="index" let-container="container" let-expanded="expanded">
  <div class="loop-config" [class.compact-loop-config]="!expanded">
    <div><small>Items to repeat over</small><strong>Items supplied when the workflow starts</strong></div>
    <div class="loop-selected"><small>For each item, do</small><strong>{{stepTitle(step.body[0])}}</strong>@if (step.body[0].kind === 'connector-operation') {<span>{{moduleAccountName(step.body[0])}}</span>}</div>
    <div class="loop-replacements" aria-label="Change the repeated step">
      <button mat-stroked-button type="button" (click)="setLoopBody(blocks, index, 'prepare-information')">Prepare each item</button>
      <button mat-stroked-button type="button" [disabled]="!localPreviewAvailable" (click)="setLoopBody(blocks, index, 'verify-local-account')">Check account</button>
    </div>
    <button mat-stroked-button type="button" [disabled]="moduleCatalogState !== 'ready'" (click)="openModulePicker(moduleLocation(container, undefined, index))">
      <mat-icon>extension</mat-icon> {{step.body[0].kind === 'connector-operation' ? 'Change connected app' : 'Use a connected app'}}
    </button>
    @if (expanded) {
      <div class="nested-step"><mat-icon>subdirectory_arrow_right</mat-icon><span><small>For each item, do</small><strong>{{stepTitle(step.body[0])}}</strong>@if (step.body[0].kind === 'connector-operation') {<small>{{moduleAccountName(step.body[0])}}</small>}</span></div>
    }
  </div>
</ng-template>
